import React, { useEffect, useRef, useState } from "react";
import { QrCode } from "./QrCode";
import { useBill } from "../context/BillContext";
import { toast } from "react-toastify";
import { formatIndianCurrency } from "../utils/Const";
import QRCode from "qrcode.react";

const BillModal = ({ billData: propBillData, closeModal }) => {
  const billRef = useRef(null);
  const [showQR, setShowQR] = useState(false);
  const { billData: contextBillData } = useBill();

  const billData = propBillData || contextBillData;
  const billUrl = billData?.orderId ? `${window.location.origin}/#/bill/${billData.orderId}` : "";

  const handlePrint = () => {
    if (!billData) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const storeName = billData?.restaurantId?.shop_type || "CloudRasoi";
    const address = billData?.restaurantId?.address || "";
    const mobile = billData?.restaurantId?.publicMobile || billData?.restaurantId?.mobile || "";
    const billDate = billData?.billDate
      ? `${new Date(billData.billDate).toLocaleDateString("en-GB")} ${new Date(billData.billDate).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}`
      : "";

    // ── Items ────────────────────────────────────────────────────────────────
    const itemsHtml = (billData?.items || []).map((item) => {
      const amount = item.price * item.qty;
      const variantLine = item.variant
        ? `<tr><td colspan="4" style="padding:0 4px 3px 2px;font-weight:700;font-size:11px;">&#x21B3; Size: ${item.variant.name}</td></tr>`
        : "";
      const addOnsLine =
        item.addOns && item.addOns.length > 0
          ? `<tr><td colspan="4" style="padding:0 4px 4px 2px;font-weight:700;font-size:11px;">+ ${item.addOns.map((a) => a.name).join(", ")}</td></tr>`
          : "";
      return `
        <tr>
          <td style="padding:5px 4px 3px 0;word-break:break-word;width:42%;font-weight:700;">${item.name}</td>
          <td style="padding:5px 2px 3px;text-align:center;width:20%;font-weight:600;">&#8377;${formatIndianCurrency(item.price)}</td>
          <td style="padding:5px 2px 3px;text-align:center;width:10%;font-weight:600;">${item.qty}</td>
          <td style="padding:5px 6px 3px 2px;text-align:right;width:28%;font-weight:700;">&#8377;${formatIndianCurrency(amount)}</td>
        </tr>
        ${variantLine}${addOnsLine}
      `;
    }).join("");

    // ── Summary Rows ─────────────────────────────────────────────────────────
    const tdL = `style="padding:3px 4px 3px 0;font-weight:600;"`;
    const tdR = `style="padding:3px 6px 3px 2px;text-align:right;font-weight:600;"`;

    const subtotalRow = `
      <tr>
        <td colspan="3" ${tdL}>Sub Total:</td>
        <td ${tdR}>&#8377;${formatIndianCurrency(billData?.subtotal)}</td>
      </tr>`;

    const discountRow = discountAmt >= 0.01 ? `
      <tr>
        <td colspan="3" ${tdL}>Discount (${discountPercent}%):</td>
        <td ${tdR}>- &#8377;${formatIndianCurrency(discountAmt)}</td>
      </tr>` : "";

    const gstRow = gstAmt >= 0.01 ? `
      <tr>
        <td colspan="3" ${tdL}>GST (${billData?.gstPercentage ?? gstPercent}%):</td>
        <td ${tdR}>+ &#8377;${formatIndianCurrency(gstAmt)}</td>
      </tr>` : "";

    const platformFeeRow = (billData?.platformFeeAmount >= 0.01 || billData?.platformFee > 0) ? `
      <tr>
        <td colspan="3" ${tdL}>Processing Fee:</td>
        <td ${tdR}>+ &#8377;${formatIndianCurrency(billData?.platformFeeAmount)}</td>
      </tr>` : "";

    const loyaltyDiscRow = billData?.loyaltyDiscountAmount > 0 ? `
      <tr>
        <td colspan="3" ${tdL}>Loyalty Discount (${billData?.loyaltyPointsRedeemed} pts):</td>
        <td ${tdR}>- &#8377;${formatIndianCurrency(billData?.loyaltyDiscountAmount)}</td>
      </tr>` : "";

    const walletRow = billData?.walletAmountRedeemed > 0 ? `
      <tr>
        <td colspan="3" ${tdL}>Paid from Wallet:</td>
        <td ${tdR}>- &#8377;${formatIndianCurrency(billData?.walletAmountRedeemed)}</td>
      </tr>` : "";

    const roundOffRow = (billData?.roundOff !== undefined && billData.roundOff !== 0) ? `
      <tr>
        <td colspan="3" ${tdL}>Round Off:</td>
        <td ${tdR}>${billData.roundOff > 0 ? "+" : "-"} &#8377;${formatIndianCurrency(Math.abs(billData.roundOff))}</td>
      </tr>` : "";

    const loyaltyEarnedNote = billData?.loyaltyPointsEarned > 0
      ? `<p style="text-align:center;margin:8px 0 4px;font-size:12px;font-weight:800;">&#127881; You earned ${billData.loyaltyPointsEarned} loyalty points!</p>`
      : "";

    const qrSection = billUrl
      ? `<div style="text-align:center;margin:10px 0 6px;">
           <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(billUrl)}&qzone=1"
                width="100" height="100" alt="QR"
                style="display:block;margin:0 auto;" />
           <p style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1px;margin:5px 0 0;">Scan for Digital Receipt</p>
         </div>`
      : "";

    // ── Full HTML ────────────────────────────────────────────────────────────
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Receipt</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body {
      font-family: 'Segoe UI', Arial, Tahoma, sans-serif;
      font-size: 13px;
      color: #000;
      background: #fff;
      width: 100%;
      padding: 3px 5mm 0 4mm;
    }
    h1 {
      font-size: 18px;
      text-align: center;
      font-weight: 900;
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .center { text-align: center; }
    .divider {
      border: none;
      border-top: 1px dashed #000;
      margin: 4px 0;
    }
    .meta-table { width: 100%; border-collapse: collapse; }
    .meta-table td { padding: 1px 0; font-weight: 600; font-size: 13px; }
    .meta-table .val { text-align: right; padding-right: 2mm; }
    .section-title {
      text-align: center;
      font-weight: 900;
      font-size: 13px;
      margin: 3px 0 3px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .items-table { width: 100%; border-collapse: collapse; }
    .items-table th {
      font-weight: 800;
      font-size: 12px;
      border-bottom: 1px solid #000;
      padding-bottom: 3px;
    }
    .items-table td { font-weight: 600; color: #000; }
    .summary-table { width: 100%; border-collapse: collapse; }
    .summary-table td { font-weight: 600; color: #000; }
    .total-row td {
      font-weight: 900;
      font-size: 15px;
      border-top: 1.5px dashed #000;
      border-bottom: 1.5px dashed #000;
      padding: 5px 0;
    }
    .total-row .total-val {
      text-align: right;
      padding-right: 2mm;
    }
    p { font-weight: 600; color: #000; }
    .footer-space { height: 120px; }
    @page { margin: 3mm 0; }
  </style>
</head>
<body>

  <h1>${storeName}</h1>
  ${address ? `<p class="center" style="font-size:11px;font-weight:600;">${address}</p>` : ""}
  ${mobile ? `<p class="center" style="font-size:11px;font-weight:600;">Mobile: ${mobile}</p>` : ""}

  <hr class="divider"/>

  <table class="meta-table">
    ${billData?.billId ? `<tr><td>Bill No:</td><td class="val">${billData.billId}</td></tr>` : ""}
    ${billData?.isOffline ? `<tr><td colspan="2" style="font-size:10px;font-style:italic;">Provisional bill (offline) — final bill no. assigned on sync</td></tr>` : ""}
    <tr><td>Date:</td><td class="val">${billDate}</td></tr>
    ${billData?.tableNo ? `<tr><td>Table No:</td><td class="val">${billData.tableNo}</td></tr>` : ""}
    ${billData?.customerName ? `<tr><td>Name:</td><td class="val">${billData.customerName}</td></tr>` : ""}
    ${billData?.customerMobile ? `<tr><td>Mobile:</td><td class="val">${billData.customerMobile}</td></tr>` : ""}
  </table>

  <hr class="divider"/>

  <p class="section-title">Order Details</p>

  <table class="items-table">
    <thead>
      <tr>
        <th style="text-align:left;width:42%;">Item</th>
        <th style="text-align:center;width:20%;">Rate</th>
        <th style="text-align:center;width:10%;">Qty</th>
        <th style="text-align:right;width:28%;padding-right:2mm;">Amt</th>
      </tr>
    </thead>
    <tbody>
      ${itemsHtml}
    </tbody>
  </table>

  <hr class="divider"/>

  <table class="summary-table">
    ${subtotalRow}
    ${discountRow}
    ${gstRow}
    ${platformFeeRow}
    ${loyaltyDiscRow}
    ${walletRow}
    ${roundOffRow}
    <tr class="total-row">
      <td colspan="3">TOTAL</td>
      <td class="total-val">&#8377;${formatIndianCurrency(billData?.totalAmount)}</td>
    </tr>
  </table>

  ${loyaltyEarnedNote}

  ${qrSection}

  <hr class="divider"/>
  <p class="center" style="font-weight:800;font-size:14px;margin:4px 0 2px;">Thank You! Visit Again</p>
  <p class="center" style="font-size:11px;font-weight:600;margin-bottom:2px;">Powered by CloudRasoi</p>

  <div class="footer-space"></div>

</body>
</html>`;

    printWindow.document.write(html);
    printWindow.document.close();

    // Wait for QR image to load before printing
    const img = printWindow.document.querySelector("img");
    if (img) {
      img.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          closeModal();
        }, 300);
      };
      img.onerror = () => {
        // Print even if QR fails to load
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          closeModal();
        }, 300);
      };
      // Fallback: if onload never fires (cached), trigger after 2s
      setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
          closeModal();
        }
      }, 2000);
    } else {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
        closeModal();
      }, 500);
    }
  };


  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleWhatsAppShare = () => {
    if (!billData?.orderId) {
      toast.error("Invalid order ID");
      return;
    }
    const storeName = billData?.restaurantId?.shop_type || "";

    const message =
      storeName.toUpperCase() +
      "\n\n" +
      "INVOICE\n\n" +
      "Thank you for dining with us.\n\n" +
      "Total Bill Amount:\nRs." +
      formatIndianCurrency(billData?.totalAmount) +
      "\n\n" +
      "View your bill at the link below:\n\n" +
      billUrl +
      "\n\n" +
      "We hope to serve you again.";

    let phone = billData?.customerMobile?.toString().trim();

    if (!phone) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    phone = phone.replace("+91", "").replace(/\s/g, "");

    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    setTimeout(()=> {
        closeModal();
    }, 500);
  };

  const subtotal = billData?.subtotal || 0;
  const discountPercent = Math.round((billData?.discountPercentage ?? 0) * 100) / 100;
  const gstPercent = billData?.gstPercentage ?? 0;

  const discountAmt = billData?.discountAmount > 0
    ? billData.discountAmount
    : subtotal * (discountPercent / 100);
  const afterDiscount = billData?.taxableAmount > 0
    ? billData.taxableAmount
    : subtotal - discountAmt;
  const gstAmt = billData?.gstAmount > 0
    ? billData.gstAmount
    : afterDiscount * (gstPercent / 100);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-white p-4 md:p-6 rounded shadow-md w-full max-w-[450px] mx-2 sm:mx-4">
        <div className="bill-slip bg-gray-100 p-4 rounded" ref={billRef}>
          <div className="print:max-w-[250px] ">
            <div className="bill">
              <h2 className="text-center text-2xl font-bold">
                {billData?.restaurantId?.shop_type || "CloudRasoi"}
              </h2>
              <div className="print:border-b-2 border-dotted border-gray-500 print:py-2 text-center">
                {billData?.restaurantId?.address && (
                  <p>{billData?.restaurantId?.address}</p>
                )}
                {billData?.restaurantId?.mobile && (
                  <p>Mobile: {billData?.restaurantId?.mobile}</p>
                )}
              </div>
              <div className="bill-details mt-3 print:border-b-2 border-dotted border-gray-500 print:py-1">
                <p className="flex justify-between">
                  <span>Bill No:</span> <span>{billData?.billId}</span>
                </p>
                {billData?.isOffline && (
                  <p className="text-[10px] italic text-amber-700">
                    Provisional bill (offline) — final bill no. assigned on sync
                  </p>
                )}
                <p className="flex justify-between">
                  <span>Date:</span>{" "}
                  <span>
                    {new Date(billData?.billDate).toLocaleDateString("en-GB")}{" "}
                    {new Date(billData?.billDate).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true })}
                  </span>
                </p>

                <div className="mt-2">
                  {billData?.customerName && (
                    <p className="flex justify-between">
                      <span>Name:</span> <span>{billData?.customerName}</span>
                    </p>
                  )}
                  {billData?.customerMobile && (
                    <p className="flex justify-between">
                      <span>Mobile:</span>{" "}
                      <span>{billData?.customerMobile}</span>
                    </p>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-semibold mt-2 text-center">
                Order Details :
              </h3>

              <div className="text-sm font-semibold border-b border-dashed pb-1">
                <div className="flex justify-between">
                  <span className="w-[40%]">Item</span>
                  <span className="w-[15%] text-center">Rate</span>
                  <span className="w-[15%] text-center">Qty</span>
                  <span className="w-[30%] text-right">Amount</span>
                </div>
              </div>

              <div className="text-sm mt-1 max-h-[180px] overflow-y-auto print:max-h-full">
                {billData?.items?.map((item, idx) => {
                  const amount = item.price * item.qty;

                  return (
                    <div
                      key={item._id || `${item.itemId}-${idx}`}
                      className="py-1 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex justify-between">
                        <span className="w-[40%] break-words leading-tight pr-1 font-medium">{item.name}</span>

                        <span className="w-[15%] text-center">₹{formatIndianCurrency(item.price)}</span>

                        <span className="w-[15%] text-center">{item.qty}</span>

                        <span className="w-[30%] text-right">
                          ₹{formatIndianCurrency(amount)}
                        </span>
                      </div>
                      {item.variant && (
                        <div className="text-[10px] text-indigo-600 font-bold pl-1 mt-0.5">
                          Size: {item.variant.name}
                        </div>
                      )}
                      {item.addOns && item.addOns.length > 0 && (
                        <div className="text-[10px] text-teal-600 font-semibold pl-1 mt-0.5">
                          + {item.addOns.map(a => a.name).join(", ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="flex justify-between font-semibold mt-4">
                <span>Sub Total:</span>
                <span>₹{formatIndianCurrency(billData?.subtotal)}</span>
              </p>

              {discountAmt >= 0.01 && (
                <p className="flex justify-between font-semibold">
                  <span>Discount ({discountPercent}%):</span>
                  <span>- ₹{formatIndianCurrency(discountAmt)}</span>
                </p>
              )}

              {gstAmt >= 0.01 && (
                <p className="flex justify-between font-semibold">
                  <span>GST ({gstPercent}%):</span>
                  <span>+ ₹{formatIndianCurrency(gstAmt)}</span>
                </p>
              )}

              {(billData?.platformFeeAmount >= 0.01 || billData?.platformFee > 0) && (
                <p className="flex justify-between font-semibold text-orange-600">
                  <span>Processing Fee:</span>
                  <span>+ ₹{formatIndianCurrency(billData?.platformFeeAmount)}</span>
                </p>
              )}

              {/* Loyalty Discount */}
              {billData?.loyaltyDiscountAmount > 0 && (
                <p className="flex justify-between font-semibold text-indigo-700">
                  <span>Loyalty Discount ({billData?.loyaltyPointsRedeemed} pts):</span>
                  <span>- ₹{formatIndianCurrency(billData?.loyaltyDiscountAmount)}</span>
                </p>
              )}

              {/* Wallet Deduction */}
              {billData?.walletAmountRedeemed > 0 && (
                <p className="flex justify-between font-semibold text-emerald-700">
                  <span>Paid from Wallet:</span>
                  <span>- ₹{formatIndianCurrency(billData?.walletAmountRedeemed)}</span>
                </p>
              )}

              {/* Round Off */}
              {billData?.roundOff !== undefined && billData.roundOff !== 0 && (
                <p className="flex justify-between font-semibold">
                  <span>Round Off:</span>
                  <span>{billData.roundOff > 0 ? "+" : "-"} ₹{formatIndianCurrency(Math.abs(billData.roundOff))}</span>
                </p>
              )}

              <div className="flex justify-between items-center py-2 mt-2 font-black text-base border-t-2 border-b-2 border-gray-800 border-dotted print:border-black print:text-lg">
                <span>Total</span>
                <span>₹{formatIndianCurrency(billData?.totalAmount)}</span>
              </div>

              {/* Print QR Code */}
              {billUrl && (
                <div className="hidden print:flex flex-col items-center justify-center mt-6">
                  <QRCode value={billUrl} size={90} renderAs="svg" />
                  <span className="text-[10px] mt-2 font-bold text-black uppercase tracking-wider">Scan for Digital Receipt</span>
                </div>
              )}

              <div className="text-center mt-6 mb-2 text-xs font-bold print:text-black">
                Thank You! Visit Again
              </div>

              {/* Points Earned on this bill */}
              {billData?.loyaltyPointsEarned > 0 && (
                <p className="text-xs text-center text-indigo-600 font-bold mt-2">
                  🎉 You earned {billData?.loyaltyPointsEarned} loyalty points on this visit!
                </p>
              )}
            </div>

            <div className="thanks my-3 text-center">
              {billData?.restaurantId?.upiId && (
                <div className="mt-4 print:hidden">
                  {!showQR ? (
                    <button
                      onClick={() => setShowQR(true)}
                      className="text-sm bg-blue-50 text-blue-600 font-semibold py-1.5 px-4 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      View QR Code to Pay
                    </button>
                  ) : (
                    <QrCode
                      upiDetails={billData?.restaurantId?.upiId}
                      totalAmount={billData?.totalAmount?.toFixed(2)}
                    />
                  )}
                </div>
              )}
              <p className="text-xl mt-4">Thanks for visiting !!</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between mt-4">
          <button
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mb-2 sm:mb-0 sm:mr-2"
            onClick={handleWhatsAppShare}
          >
            Share on WhatsApp
          </button>
          <button
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded mb-2 sm:mb-0 sm:mr-2"
            onClick={handlePrint}
          >
            Print
          </button>
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mb-2 sm:mb-0 sm:mr-2"
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillModal;
